import React from 'react';
import AlertMessage from '../../AlertMessage';
import SampleCollectionForm from '../../sample-collection/SampleCollectionForm';
import TestPrepInfo from '../../sample-collection/TestPrepInfo';
import EditIcon from '../EditIcon';
import EditSection from '../EditSection';
import { getLocale } from '../../util/gearOrderUtil';

const SampleCollectionInformation = ({
	gearOrder,
	requisition,
	omsRequisition,
	userType,
	editingReq,
	editingSection,
	cancelEdit,
	saveEdit,
	isEditSaveInProgress,
	editRef,
	inlineSaveError,
	getWorkflowStep,
	canEditReq,
	editSection,
	lastSavedReq,
	lastSavedSection
}) => {
	// PITL orders handle Sample Collection on the summary page
	if (gearOrder.physicianInTheLoop) {
		return null;
	}

	if (editingReq === `${requisition?.id}` && editingSection === 'collection') {
		return (
			<div className="mt-20" data-step={`collection-${requisition.id}`}>
				<EditSection
					title="Sample Collection Information"
					onCancel={cancelEdit}
					onSave={saveEdit}
					isEditSaveInProgress={isEditSaveInProgress}
				>
					<SampleCollectionForm
						ref={editRef}
						gearRequisition={requisition}
						omsRequisition={omsRequisition}
						gearOrder={gearOrder}
						userType={userType}
					/>
					{inlineSaveError && <div className="validation-error text-danger text-right">{inlineSaveError}</div>}
				</EditSection>
			</div>
		);
	}

	if (userType === 'myGDX') {
		return (
			<ClientDisplay
				gearOrder={gearOrder}
				requisition={requisition}
				omsRequisition={omsRequisition}
				userType={userType}
				editingReq={editingReq}
				editingSection={editingSection}
				cancelEdit={cancelEdit}
				saveEdit={saveEdit}
				isEditSaveInProgress={isEditSaveInProgress}
				editRef={editRef}
				inlineSaveError={inlineSaveError}
				getWorkflowStep={getWorkflowStep}
				canEditReq={canEditReq}
				editSection={editSection}
				lastSavedReq={lastSavedReq}
				lastSavedSection={lastSavedSection}
			/>
		);
	}

	let collectionDetails = null;
	let hasReqNumber = gearOrder?.requisitions?.some(req => req.reqNumber && req.reqNumber > 0);
	let sampleCollectionNotRequired = userType === 'prc' && !hasReqNumber;
	if(sampleCollectionNotRequired) {
		collectionDetails = null;
	} else if (requisition.dateLastSampleCollected) {
		collectionDetails = (
			<p className="mb-10">
				<strong>Date Last Sample Collected:</strong>{' '}
				{new Date(requisition.dateLastSampleCollected + 'T00:00:00Z').toLocaleDateString(getLocale(), {
					weekday: 'long',
					year: 'numeric',
					month: 'long',
					day: 'numeric',
					timeZone: 'UTC'
				})}
				{canEditReq && (
					<EditIcon
						title="Sample Collection Information"
						onClick={editSection}
						editKey={`collection_${requisition.id}`}
						className="btn btn-edit btn-primary"
						editEl="Edit"
					/>
				)}
				{lastSavedReq === `${requisition.id}` && lastSavedSection === 'collection' && (
					<small>
						<span className="ms-2 text-success">
							Changes saved<i className="ms-1 fa fa-check-circle"></i>
						</span>
					</small>
				)}
			</p>
		);
	} else {
		collectionDetails = (
			<>
				<span><strong>Sample Collection:</strong> Not provided</span>
				<AlertMessage type="danger" additionalCssClass="alert-icon-warning mt-10">
					<span>
						Click{' '}
						<button
							type="button"
							className="btn-link btn-unstyled link-underline"
							onClick={() => editSection(`collection_${requisition.id}`)}
						>
							<strong>here</strong>
						</button>{' '}
						to provide missing Sample Collection information.
					</span>
				</AlertMessage>
			</>
		);
	}

	let patientInstructions = null;
	if (requisition.patientInstructions) {
		patientInstructions = (
			<>
				<p className="patient-instructions">
					<strong>Patient Instructions:</strong> {requisition.patientInstructions}
				</p>
			</>
		);
	}

	return (
		<div data-step={`collection-${requisition.id}`}>
			{collectionDetails}
			{patientInstructions}
		</div>
	);
};

const ClientDisplay = ({ requisition, userType, canEditReq, editSection, lastSavedReq, lastSavedSection }) => {
	let dateCollectedSection = null;
	if (requisition.dateLastSampleCollected) {
		dateCollectedSection = (
			<div className="row">
				<div className="col-sm-12">
					<strong>Date Last Sample Collected:</strong>{' '}
					{new Date(requisition.dateLastSampleCollected + 'T00:00:00Z').toLocaleDateString(getLocale(), {
						weekday: 'long',
						year: 'numeric',
						month: 'long',
						day: 'numeric',
						timeZone: 'UTC'
					})}
				</div>
			</div>
		);
	} else if (requisition.reqNumber) {
		dateCollectedSection = (
			<AlertMessage type="danger">
				<strong>Incomplete. The patient will need to provide this information.</strong>
			</AlertMessage>
		);
	}

	let patientInstructions = requisition.patientInstructions && (
		<div className="row">
			<div className="col-sm-12">
				<p>
					<strong>Patient Instructions:</strong> {requisition.patientInstructions}
				</p>
			</div>
		</div>
	);

	/*
	 * Show empty patient instructions if the sample has not been collected.
	 * This indicates to the client that they can edit this section to add patient instructions.
	 */
	if (!patientInstructions && !requisition.dateLastSampleCollected && !requisition.accessionNo) {
		patientInstructions = (
			<div className="row">
				<div className="col-sm-12">
					<p>
						<strong>Patient Instructions:</strong>{' '}
					</p>
				</div>
			</div>
		);
	}

	let clinicalFindings = requisition.clinicalFindings && (
		<div className="row">
			<div className="col-sm-12">
				<p>
					<strong>Clinical Findings/Clinical Impressions:</strong> {requisition.clinicalFindings}
				</p>
			</div>
		</div>
	);

	let routeNumber = requisition.routeNumber && (
		<div className="row">
			<div className="col-sm-12">
				<p>
					<strong>Routing Number:</strong> {requisition.routeNumber}
				</p>
			</div>
		</div>
	);

	return (
		<div id="collectionInfo" data-step={`collection-${requisition.id}`}>
			<h4>
				Sample Collection Information
				{canEditReq && (
					<EditIcon
						title="Sample Collection Information"
						onClick={editSection}
						editKey={`collection_${requisition.id}`}
						className="btn btn-edit btn-primary"
						editEl="Edit"
					/>
				)}
				{lastSavedReq === `${requisition.id}` && lastSavedSection === 'collection' && (
					<small>
						<span className="ms-2 text-success">
							Changes saved<i className="ms-1 fa fa-check-circle"></i>
						</span>
					</small>
				)}
			</h4>
			<TestPrepInfo userType={userType} />
			{dateCollectedSection}
			{patientInstructions}
			{clinicalFindings}
			{routeNumber}
		</div>
	);
};

export default SampleCollectionInformation;
